import {
  SET_WIDGET, CLONE_WIDGET, DELETE_WIDGET, SET_TARGET_WIDGET,
  UPDATE_WIDGET_STYLING, UPDATE_WIDGET_SETTINGS,
  UPDATE_WIDGET_DETAILS, UPDATE_ALL_WIDGETS_SETTINGS,
  WIDGETS_CLEAR, UPDATE_WIDGET_THEME,
} from '../_types'
import defaultWidgetTemplates from '@components/Widgets/templateDefaults'
import campaignAPI from '@api/campaign'
import { formatWidgetForApi, formatWidgetFromApi } from '@utils/formatWidgetApi'
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import defaultTheme from '@components/Widgets/Templates/Base/_themeBase'

const baseTheme = defaultTheme

const getDefaultState = () => {
  return {
    targetWidgetID: null,
    widgetItems: {},
    widgetItemsList: [],
    // Templates
    defaultWidgetTemplates,
  }
}
// Save state at the time before the change
let pendingUndoState = null

const debouncedPushWidgetToUndoStack = _debounce(
  (dispatch, getWidgetState) => {
    if (pendingUndoState) {
      dispatch('campaign/widget/undoRedo/pushUndoStack', pendingUndoState,
        { root: true })
      pendingUndoState = null
    }
  }, 400)

export const state = getDefaultState()

export const getters = {
  /**
   * Returns widget fields per widget
   */
  getWidgetFields: (state, getters) => {
    const targetWidget = getters.targetWidget
    if (!targetWidget) return []

    return Object.values(targetWidget.steps).reduce((acc, step) => {
      Object.values(step.contentItems).forEach(contentItem => {
        if (contentItem.category === 'form') {
          acc.push(contentItem)
        }
      })
      return acc
    }, [])
  },
  /**
   * Returns widget fields per widget
   */
  getWidgetVouchers: (state, getters) => {
    const targetWidget = getters.targetWidget
    if (!targetWidget) return []

    return Object.values(targetWidget.steps).reduce((acc, step) => {
      Object.values(step.contentItems).forEach(contentItem => {
        if (contentItem.type === 'voucher') {
          acc.push(contentItem)
        }
      })
      return acc
    }, [])
  },
  /**
   * An Array of all the widgets in the campaign
   */
  getWidgetItems: (state) => state.widgetItems,
  /**
   * An Array of all existing widget IDs
   */
  getWidgetItemsList: (state) => state.widgetItemsList,
  /**
   * An Array of all existing widget types
   */
  getDefaultWidgetTemplateByType: (state) => (type) => {
    return state.defaultWidgetTemplates.find(function (obj) {
      return obj.type === type
    })
  },
  /**
   * Get current campaign widget count
   */
  getWidgetCount: (state) => {
    if (state.widgetItems) {
      return state.widgetItemsList.length
    }
  },
  /**
   * The currently targeted widget object
   */
  targetWidget: (state) => {
    return state.widgetItems[state.targetWidgetID]
  },
  /**
   * The currently targeted widget object
   */
  targetWidgetType: (state) => {
    return state.widgetItems[state.targetWidgetID].type
  },
  /**
   * The currently targeted widget Steps object
   */
  targetWidgetSteps: (state) => {
    return state.widgetItems[state.targetWidgetID].steps
  },
  /**
   * The currently targeted widget StepsList array
   */
  targetWidgetStepsList: (state) => {
    return state.widgetItems[state.targetWidgetID].stepsList
  },
}

export const mutations = {
  [SET_WIDGET](state, { widget }) {
    state.widgetItemsList.push(widget.id)
    this._vm.$set(state.widgetItems, widget.id, widget)
  },
  [CLONE_WIDGET](state, { newWidget, index }) {
    state.widgetItemsList.splice(index + 1, 0, newWidget.id)
    this._vm.$set(state.widgetItems, newWidget.id, newWidget)
  },
  [DELETE_WIDGET](state, { id, index }) {
    state.widgetItemsList.splice(index, 1)
    this._vm.$delete(state.widgetItems, id)

    for (let i = state.widgetItemsList.length - 1; i--;) {
      if (state.widgetItemsList[i].id === id) state.widgetItemsList.splice(i, 1)
    }
  },
  [SET_TARGET_WIDGET](state, id) {
    state.targetWidgetID = id
  },
  [UPDATE_WIDGET_STYLING](state, { id, path, styling }) {
    state.widgetItems[id].styling[path] = styling
  },
  [UPDATE_WIDGET_SETTINGS](state, { id, path, settings }) {
    state.widgetItems[id].settings[path] = settings
  },
  [UPDATE_WIDGET_DETAILS](state, { id, path, value }) {
    state.widgetItems[id][path] = value
  },
  [UPDATE_WIDGET_THEME](state, { id, path, value }) {
    let widgetTheme = state.widgetItems[id].theme
    // 1. If this widget has no 'theme', copy the entire base theme
    if (!widgetTheme) {
      this._vm.$set(state.widgetItems[id], 'theme', { ...baseTheme })
      widgetTheme = state.widgetItems[id].theme
    }

    // 2. Walk the path (e.g. ["button","backgroundColor"]) to ensure each sub-object exists
    let current = widgetTheme
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i]
      if (!current[key]) {
        // If it's in the base theme, copy that sub-object; otherwise, just create an empty object
        current[key] = baseTheme[key] ? { ...baseTheme[key] } : {}
      }
      current = current[key]
    }

    // 3. Finally, set the last property in the chain
    const lastKey = path[path.length - 1]
    this._vm.$set(current, lastKey, value)
  },
  [UPDATE_ALL_WIDGETS_SETTINGS](state, { path, settings }) {
    Object.values(state.widgetItems).forEach(widget => {
      widget.settings[path] = settings
    })
  },
  RESTORE_WIDGET_STATE(state, widgetState) {
    // Replace the widget state with the one from the undo/redo stack
    this._vm.$set(state.widgetItems, widgetState.id, widgetState)
  },
  [WIDGETS_CLEAR](state) {
    Object.assign(state, getDefaultState())
  },
}

export const actions = {
  setWidgetItems({ commit, dispatch }, itemList) {
    itemList.forEach((widget) => {
      commit(SET_WIDGET, { widget })
    })

    dispatch('setTargetWidget', itemList[0].id)
  },
  getWidget({ commit }, campaignID) {
    return campaignAPI.getWidget(campaignID).then((response) => {
      // commit(WIDGET_FETCH_SUCCESS, response.data);
    }).catch((error) => {
      console.log('GET WIDGET ERROR',
        error.response.data[Object.keys(error.response.data)[0]][0])
    })
  },
  setTargetWidget({ commit, state, dispatch }, id) {
    if (state.targetWidgetID !== id) {
      commit(SET_TARGET_WIDGET, id)
      dispatch('campaign/step/setTargetStep',
        state.widgetItems[id].stepsList[0], { root: true })
    }
  },
  createWidget(
    { state, dispatch, commit, getters, rootGetters }, { id }) {
    if (typeof getters.getWidgetCount === 'undefined' ||
      getters.getWidgetCount < 2) {
      const widgetTemplate = _cloneDeep(
        rootGetters['templates/getWidgetTemplateByID'](id))

      widgetTemplate.campaignID = rootGetters['campaign/targetCampaignID']
      widgetTemplate.trafficShare = 0.5
      const settings = [
        'animation',
        'trigger',
        'triggerSeconds',
        'ribbonText',
        'ribbonPosition',
        'ribbonColor',
        'ribbonBgColor',
        'position']

      settings.forEach(setting => {
        widgetTemplate.settings[setting] = getters.targetWidget.settings[setting]
      })

      return campaignAPI.createWidget(widgetTemplate).then((response) => {
        widgetTemplate.id = response.data.id
        const widget = formatWidgetFromApi(widgetTemplate)

        commit(SET_WIDGET, { widget })
        dispatch('setTargetWidget', widget.id)
      }).catch((error) => {
        console.log(
          'CREATE WIDGET ERROR',
          error.response.data[Object.keys(error.response.data)[0]][0],
        )
      })
    } else {
      this.$message.warning('Limit reached.')
    }
  },
  async saveWidget({ commit, state }, id) {
    const widget = formatWidgetForApi(state.widgetItems[id])
    try {
      const response = await campaignAPI.saveWidget(widget)

      return response
    } catch (error) {
      console.error('Error saving widget:', error)
      throw new Error('Failed to save widget')
    }
  },
  cloneWidget({ dispatch, commit, getters, state }, { id, index }) {
    if (getters.getWidgetCount < 2) {
      const newWidgetFormatted = formatWidgetForApi(state.widgetItems[id])

      try {
        return campaignAPI.createWidget(newWidgetFormatted).then((response) => {
          const newWidget = _cloneDeep(state.widgetItems[id])
          newWidget.id = response.data.id
          newWidget.trafficShare = 0.5

          commit(CLONE_WIDGET, { newWidget, index })
          dispatch('setTargetWidget', newWidget.id)
        })
      } catch (error) {
        console.error('Error cloning widget:', error)
        this.$message.warning('Error occurred.')
      }
    } else {
      // ToDo $message errors out as undefined
      this.$message.warning('Limit reached.')
    }
  },
  deleteWidget({ commit, dispatch, state, getters }, { id, index }) {
    if (getters.getWidgetCount > 1) {
      for (const key in state.widgetItems) {
        if (Object.prototype.hasOwnProperty.call(state.widgetItems, key) &&
          parseInt(key) !== id) {
          dispatch('setTargetWidget', parseInt(key))
          return campaignAPI.deleteWidget(id).then(() => {
            commit(DELETE_WIDGET, { id, index })
          })
        }
      }
    }
  },
  updateWidgetStyling({ commit, dispatch }, { id, path, styling }) {
    if (!pendingUndoState) {
      pendingUndoState = _cloneDeep(state.widgetItems[id])
    }
    commit(UPDATE_WIDGET_STYLING, { id, path, styling })
    debouncedPushWidgetToUndoStack(dispatch)
  },
  updateWidgetSettings({ commit, dispatch }, { id, path, settings }) {
    if (!pendingUndoState) {
      pendingUndoState = _cloneDeep(state.widgetItems[id])
    }
    commit(UPDATE_WIDGET_SETTINGS, { id, path, settings })
    debouncedPushWidgetToUndoStack(dispatch)
  },
  updateWidgetDetails({ commit, dispatch }, { id, path, value }) {
    commit(UPDATE_WIDGET_DETAILS, { id, path, value })
  },
  updateWidgetTheme({ commit }, { id, path, value }) {
    commit(UPDATE_WIDGET_THEME, { id, path, value })
  },
  updateAllWidgetsSettings({ commit }, { path, settings }) {
    commit(UPDATE_ALL_WIDGETS_SETTINGS, { path, settings })
  },
}
